import React, { useState } from 'react';
import { Folder, Shield, TrendingUp, Target, Clock, Award, CheckCircle, ChevronDown, ChevronUp, BarChart, FileText, Bell } from 'lucide-react';
import RegistrationModal from '../../components/RegistrationModal';
import SEO from '../../components/SEO';
import ServicePricing from '../../components/ServicePricing';
import CTASection from '../../components/CTASection';

const CopyrightPortfolioPage = () => {
  const [openFAQ, setOpenFAQ] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [modalMode, setModalMode] = useState(null);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const openModal = (mode) => {
    setModalMode(mode);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setModalMode(null);
  };

  const benefits = [
    {
      icon: Folder,
      title: "Centralized Management",
      description: "Organize all your copyrights in one secure platform with comprehensive tracking and documentation."
    },
    {
      icon: Shield,
      title: "Proactive Protection",
      description: "Continuous monitoring for infringement with automated alerts and rapid enforcement response."
    },
    {
      icon: TrendingUp,
      title: "Revenue Optimization",
      description: "Maximize licensing opportunities and royalty collection across your entire copyright portfolio."
    },
    {
      icon: Bell,
      title: "Renewal Management",
      description: "Never miss critical deadlines with automated reminders for renewals and maintenance requirements."
    },
    {
      icon: BarChart,
      title: "Portfolio Analytics",
      description: "Detailed reporting on portfolio value, licensing revenue, and infringement incidents."
    },
    {
      icon: Award,
      title: "Strategic Planning",
      description: "Expert guidance on portfolio growth, asset acquisition, and monetization strategies."
    }
  ];

  const services = [
    {
      title: "Registration Management",
      description: "Comprehensive registration tracking and renewal coordination for all copyrights.",
      features: ["Registration tracking", "Renewal reminders", "Certificate storage", "Status monitoring"]
    },
    {
      title: "Infringement Monitoring",
      description: "Automated surveillance across digital platforms to detect unauthorized use.",
      features: ["Web monitoring", "Image searches", "Content tracking", "Alert notifications"]
    },
    {
      title: "Licensing Administration",
      description: "Full-service management of licensing agreements and royalty collection.",
      features: ["License tracking", "Royalty audits", "Agreement management", "Revenue reporting"]
    },
    {
      title: "Enforcement Coordination",
      description: "Streamlined enforcement across your portfolio with consistent strategy.",
      features: ["Takedown notices", "Cease & desist", "Settlement tracking", "Litigation support"]
    }
  ];

  const process = [
    {
      step: "1",
      title: "Portfolio Audit",
      description: "Comprehensive review of all your creative works, identifying unregistered copyrights and protection gaps.",
      timeline: "1-2 weeks"
    },
    {
      step: "2",
      title: "System Setup",
      description: "Implement tracking systems, register unprotected works, and establish monitoring protocols for your portfolio.",
      timeline: "2-3 weeks"
    },
    {
      step: "3",
      title: "Monitoring & Growth",
      description: "Activate automated monitoring across digital platforms and continuously optimize your portfolio protection strategy.",
      timeline: "Ongoing"
    }
  ];

  const packages = [
    {
      name: "Basic Portfolio",
      price: "$499/mo",
      description: "Up to 25 copyrights",
      features: [
        "Registration tracking",
        "Renewal reminders",
        "Document storage",
        "Quarterly reports",
        "Email support",
        "Basic monitoring"
      ],
      popular: false
    },
    {
      name: "Professional Portfolio",
      price: "$999/mo",
      description: "Up to 100 copyrights",
      features: [
        "Everything in Basic",
        "Infringement monitoring",
        "License management",
        "Monthly reports",
        "Priority support",
        "Enforcement coordination",
        "Revenue analytics"
      ],
      popular: true
    },
    {
      name: "Enterprise Portfolio",
      price: "$2,499/mo",
      description: "Unlimited copyrights",
      features: [
        "Everything in Professional",
        "Dedicated account manager",
        "Custom monitoring rules",
        "Real-time alerts",
        "Strategic consulting",
        "24/7 support",
        "API access"
      ],
      popular: false
    }
  ];

  const faqs = [
    {
      question: "What is copyright portfolio management and who needs it?",
      answer: "Copyright portfolio management is the systematic organization, protection, and monetization of multiple copyrighted works. It's essential for: (1) businesses with extensive creative content (publishers, media companies, software firms), (2) creators with large work catalogs (photographers, authors, musicians), (3) companies licensing content to multiple parties, (4) organizations managing legacy copyrights, or (5) anyone with 10+ copyrighted works requiring active management. Professional management prevents loss of rights, maximizes revenue, and ensures consistent enforcement."
    },
    {
      question: "How does copyright portfolio management differ from individual copyright services?",
      answer: "Individual services handle single copyrights or transactions (registration, single enforcement). Portfolio management provides comprehensive, ongoing oversight of all copyrights including: centralized registration tracking, automated renewal reminders, coordinated infringement monitoring across all works, unified licensing strategy, consolidated reporting and analytics, and strategic planning for portfolio growth. It's the difference between managing individual assets and having a complete asset management program with economies of scale."
    },
    {
      question: "What does copyright infringement monitoring include?",
      answer: "Our monitoring service uses automated tools and manual review to detect unauthorized use across: (1) websites and blogs (content scraping, reproduction), (2) social media platforms (unauthorized posting), (3) e-commerce sites (counterfeit products), (4) file-sharing networks (piracy), (5) search engines (indexed infringement), and (6) streaming platforms (unauthorized distribution). You receive alerts with evidence and recommended actions. We can immediately deploy enforcement measures for confirmed infringement."
    },
    {
      question: "How much does portfolio management cost?",
      answer: "Cost depends on portfolio size and service level. Basic management for 25 works starts at $499/month. Professional management for 100 works costs $999/month. Enterprise solutions for unlimited works start at $2,499/month. These fees cover registration tracking, monitoring, reporting, and support—significantly less than the cost of managing copyrights individually or the losses from undetected infringement. Many clients recover the cost through improved licensing revenue and reduced infringement."
    },
    {
      question: "Can you manage copyrights that weren't registered through your firm?",
      answer: "Yes, we manage portfolios regardless of where registrations originated. During onboarding, we collect all registration certificates, applications, and documentation. We verify registration status with the Copyright Office and identify any unregistered works requiring protection. We can handle legacy portfolios, inherited copyrights, acquired assets, and works created by previous attorneys. Our goal is comprehensive management of your entire portfolio regardless of its history."
    },
    {
      question: "How do you handle licensing management for my portfolio?",
      answer: "We maintain a centralized database of all license agreements including: licensee information, licensed works, territories, duration, royalty rates, payment schedules, and renewal options. We track payment obligations, flag late payments, conduct periodic royalty audits, manage renewals and terminations, and identify new licensing opportunities. You receive regular revenue reports with licensing income by work, licensee, and territory. We ensure maximum monetization of your portfolio while maintaining compliance."
    },
    {
      question: "What happens when infringement is detected in my portfolio?",
      answer: "When monitoring detects potential infringement, we: (1) document evidence (screenshots, purchases, downloads), (2) assess infringement severity and commercial impact, (3) recommend enforcement strategy (DMCA takedown, cease and desist, settlement, litigation), (4) execute approved actions immediately, and (5) track enforcement until resolution. You have final approval on enforcement decisions. Our coordinated approach across your portfolio ensures consistent enforcement and builds deterrence against serial infringers."
    },
    {
      question: "Do you provide reporting and analytics for my copyright portfolio?",
      answer: "Yes, you receive detailed reports including: (1) portfolio inventory with registration status and key dates, (2) licensing revenue by work and licensee, (3) infringement incidents detected and resolved, (4) enforcement actions taken and results, (5) portfolio valuation estimates, (6) renewal deadlines and upcoming actions, and (7) recommendations for portfolio optimization. Reporting frequency depends on your package (quarterly, monthly, or real-time). Enterprise clients receive custom dashboards and API access."
    },
    {
      question: "Can you help grow and expand my copyright portfolio?",
      answer: "Absolutely. Portfolio growth services include: (1) identifying unprotected works requiring registration, (2) reviewing new creations for registration eligibility, (3) assessing acquisition opportunities (purchasing copyrights from others), (4) evaluating licensing-in options (obtaining licenses to expand your offerings), (5) strategic planning for portfolio development, and (6) competitive analysis of similar portfolios. We help you build a comprehensive portfolio that supports your business objectives and maximizes asset value."
    },
    {
      question: "How secure is my copyright portfolio information?",
      answer: "We employ bank-level security for all portfolio data including: (1) encrypted data storage and transmission, (2) secure cloud infrastructure with redundant backups, (3) role-based access controls, (4) regular security audits and penetration testing, (5) attorney-client privilege protection for legal communications, and (6) compliance with data protection regulations. Only authorized team members access your portfolio. You maintain ownership of all documentation and can export your data anytime. We take portfolio security as seriously as you do."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEO 
        title="Copyright Portfolio Management - Protect & Monetize Your Creative Assets"
        description="Organize, protect, and monetize your creative works with expert copyright portfolio management services."
        keywords="copyright portfolio management, copyright tracking, infringement monitoring, licensing management, copyright assets"
      />

      {/* Hero Section */}
      <section className="relative text-white py-20 overflow-hidden">
            <img src="https://images.unsplash.com/photo-1456324504439-367cee3b3c32?w=900w=1600&q=80q=50" alt="" loading="eager" fetchpriority="high" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#2F1A8E]/90 via-[#1a0f5e]/85 to-[#0d0830]/90"></div>
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF035B]/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#FF035B]/5 rounded-full blur-2xl"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#FF035B]/20 text-[#FF035B] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Folder className="w-4 h-4" />
                <span>Portfolio Management Services</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Professional Copyright Portfolio Management
              </h1>
              <p className="text-xl mb-8 text-gray-300">
                Organize, protect, and monetize your entire copyright portfolio with comprehensive management services. From registration tracking to infringement monitoring and licensing optimization.
              </p>
              <div className="flex flex-wrap gap-4">
                <button 
                  onClick={() => openModal('registration')}
                  className="bg-[#FF035B] hover:bg-[#2F1A8E] text-white px-8 py-4 rounded-lg font-semibold transition-all hover:shadow-xl hover:scale-105"
                >
                  Start Managing Portfolio
                </button>
                <button 
                  onClick={() => { if (window.cd360Api && window.cd360Api.liveChat) window.cd360Api.liveChat.showWebWidget(); }}
                  className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all"
                >
                  Schedule Consultation
                </button>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>Automated Monitoring</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>Revenue Optimization</span>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
              <h3 className="text-lg font-bold mb-6">Portfolio Benefits</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block">Centralized Control</strong>
                    <span className="text-gray-300">All copyrights managed in one secure system with complete visibility</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block">Proactive Protection</strong>
                    <span className="text-gray-300">Automated monitoring detects infringement before it causes major damage</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block">Maximize Revenue</strong>
                    <span className="text-gray-300">Optimized licensing and enforcement generate more income from your assets</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Portfolio Management
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional oversight to protect and grow your copyright assets
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="group bg-white hover:bg-gradient-to-br hover:from-[#FF035B] hover:to-[#2F1A8E] rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:scale-105 cursor-pointer hover:shadow-xl transition-all border border-gray-100">
                <benefit.icon className="w-12 h-12 text-[#FF035B] group-hover:text-white transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-white transition-colors duration-500 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 group-hover:text-white/90 transition-colors duration-500 leading-relaxed group-hover:text-white/90 transition-colors duration-500">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What's Included in Portfolio Management
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Complete copyright lifecycle management and protection
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-all">
                <h3 className="text-lg font-bold text-gray-900 group-hover:text-white transition-colors duration-500 mb-3">{service.title}</h3>
                <p className="text-gray-600 group-hover:text-white/90 transition-colors duration-500 mb-6">{service.description}</p>
                <div className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-gray-700">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Management Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From portfolio audit to ongoing optimization
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-gradient-to-br from-[#FF035B] to-[#2F1A8E] text-white rounded-xl p-6 shadow-lg">
                  <div className="text-4xl font-bold mb-2 opacity-50">{item.step}</div>
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-sm mb-4 text-white/90">{item.description}</p>
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <Clock className="w-4 h-4" />
                    {item.timeline}
                  </div>
                </div>
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-[#FF035B] to-transparent"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <ServicePricing showPrices={false} title="Copyright Portfolio Services" subtitle="Get a personalized quote for your specific needs" />


      {/* FAQ */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about copyright portfolio management
            </p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-all"
                >
                  <span className="font-semibold text-gray-900 pr-8">{faq.question}</span>
                  {openFAQ === index ? (
                    <ChevronUp className="w-5 h-5 text-[#FF035B] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openFAQ === index && (
                  <div className="px-8 pb-6">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#FF035B] to-[#2F1A8E] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Optimize Your Copyright Portfolio?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Stop managing copyrights one at a time. Get comprehensive portfolio management that protects your assets and maximizes revenue.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button 
              onClick={() => openModal('registration')}
              className="bg-white text-[#FF035B] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all hover:shadow-xl"
            >
              Start Portfolio Management
            </button>
            <button 
              onClick={() => { if (window.cd360Api && window.cd360Api.liveChat) window.cd360Api.liveChat.showWebWidget(); }}
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#FF035B] transition-all"
            >
              Schedule Consultation
            </button>
          </div>
        </div>
      </section>

      <CTASection />

      <RegistrationModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialType="registration"
      />
    </div>
  );
};

export default CopyrightPortfolioPage;
